import './applianceSummary.scss';

import React, { useEffect, useState } from 'react';
import Stars, {
    AdditionalInfoDisplayLocation,
    AdditionalInfoType,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Reviews/Stars';
import { graphql, useStaticQuery } from 'gatsby';

import { Button } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import ModalAppliancePreviewVisao from './ModalAppliancePreviewVisao';
import arrowIconGray from '@progymedia/sbi-web-occ/src/images/icons/Icon-Fleche-Gray.svg';
import arrowIconVentis from '@progymedia/sbi-web-occ/src/images/icons/Icon-Fleche-VentisColor.svg';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useProductReviews } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useProductReviews';
import { useTranslation } from 'react-i18next';

type ApplianceSummaryProps = {
    appliance: any;
    goToReviews: () => void;
    scrollToApplianceDetails: (section: string) => void;
};

export default function ApplianceSummary(props: ApplianceSummaryProps) {
    const { appliance, goToReviews, scrollToApplianceDetails } = props;
    const { govCreditData, heatingAreaDocument } = useStaticQuery(
        graphql`
            query {
                govCreditData: allProductsJson(
                    filter: { code: { eq: "occ_tax-credit-usa" } }
                ) {
                    nodes {
                        category
                        code
                        title {
                            en_US
                        }
                        documents {
                            en_US
                        }
                    }
                }
                heatingAreaDocument: allProductsJson(
                    filter: { code: { eq: "occ_carte-superficie-chauffage" } }
                ) {
                    nodes {
                        title {
                            en_US
                            fr_CA
                        }
                        documents {
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        `
    );
    const { t } = useTranslation('templatePages');
    const { currentLocale } = usePageLocalization();
    const [isShowVisao, setIsShowVisao] = useState<boolean>(false);
    const [toggler3D, setToggler3D] = useState(false);
    const [isStartAR, setIsStartAR] = useState(false);

    const mobileDetect = useMobileDetect();
    let mdDescription =
        appliance?.description?.valuesMarkdownRemark ?? appliance?.description;
    const [isHover, setHover] = useState(false);
    const [price] = useProductPrice(appliance?.code);
    const displayPrice = currentLocale !== 'en_US';
    const govCreditUsa = govCreditData?.nodes[0];

    const productReviews = useProductReviews(appliance?.code);

    const visaoId = appliance.attributes?.visao_id?.values?.[0]?.['en_CA'];
    const showInMySpaceButton =
        !appliance?.slug?.['en_CA']?.includes('inserts') &&
        !appliance?.slug?.['en_CA']?.includes('fireplace');

    useEffect(() => {
        setIsShowVisao(!!visaoId);
    }, [visaoId]);

    const handleModalClose = () => {
        setToggler3D(false);
    };

    const showHeatingAreaDocument = () => {
        return appliance.category ===
            '/OCC/Heaters/High-Efficiency Fireplaces/' ||
            appliance.category == '/OCC/Heaters/Wood Stoves/' ||
            appliance.category == '/OCC/Heaters/Pellet stove/' ||
            appliance.category == '/OCC/Heaters/Wood Inserts/'
            ? true
            : false;
    };

    const showVisaoModal = (newStartAR: boolean) => {
        if (mobileDetect.isMobile() && newStartAR) {
            navigateToVisaoPage(visaoId!);
        } else {
            setIsStartAR(newStartAR);
            setToggler3D(true);
        }
    };

    const navigateToVisaoPage = (visaoId: string) => {
        const lang = currentLocale === 'fr_CA' ? 'fr' : 'en';
        const a: HTMLAnchorElement = document.createElement('a');
        a.target = '_blank';
        a.href = `https://demo.visao.ca/viewer/${visaoId}?lang=${lang}&ar=true`;
        a.click();
    };

    const showVisaoButtons = (isMobile: boolean) => {
        const className = isMobile ? 'mobile' : 'desktop';

        return (
            <div className={`visao-section ${className}`}>
                <Button
                    className={`visao-btn ${className}`}
                    label={t('appliance-page.see-product-3d')}
                    onClick={() => showVisaoModal(false)}
                />
                {showInMySpaceButton && (
                    <Button
                        className={`visao-btn ${className}`}
                        label={t('appliance-page.see-product-space')}
                        onClick={() => showVisaoModal(true)}
                    />
                )}
            </div>
        );
    };

    return (
        <div className='appliance-summary'>
            {isShowVisao && mobileDetect.isMobile() && showVisaoButtons(true)}

            <h1 className='title'>
                <span className='code'>{appliance?.code}</span>
                <span className='category'>{`${
                    appliance?.category_localized[currentLocale]?.split('/')[3]
                } Ventis`}</span>
                <span className='name'>{appliance?.title[currentLocale]}</span>
            </h1>
            <div className='rating-section'>
                {productReviews && productReviews.count > 0 && (
                    <div className='rating'>
                        <Stars
                            rating={productReviews.average}
                            reviewsCount={productReviews.count}
                            additionalInfoDisplayLocation={
                                AdditionalInfoDisplayLocation.RIGHT
                            }
                            additionalInfoType={
                                AdditionalInfoType.NUMBER_OF_REVIEWS
                            }
                            onAdditionalInfoClick={goToReviews}
                        />
                    </div>
                )}
                <button
                    className='button see-reviews'
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    onClick={goToReviews}
                >
                    {t('appliance-page.see-reviews')}
                    <img
                        src={isHover ? arrowIconVentis : arrowIconGray}
                        style={{}}
                        alt=''
                    />
                </button>
            </div>

            <div
                className='description desc-list'
                dangerouslySetInnerHTML={{
                    __html: mdDescription[currentLocale],
                }}
            ></div>
            {currentLocale === 'en_US' && (
                <a
                    className='link'
                    rel='noreferrer'
                    target='_blank'
                    href={`/file/${govCreditUsa?.documents?.[0][currentLocale]}`}
                >
                    <span>{govCreditUsa?.title?.[currentLocale]}</span>
                </a>
            )}
            <span
                className='link'
                onClick={() =>
                    scrollToApplianceDetails(t('appliance-page.specs-title'))
                }
            >
                {t('appliance-page.see-specs')}
            </span>
            {appliance?.documents && (
                <span
                    className='link'
                    onClick={() =>
                        scrollToApplianceDetails(
                            t('appliance-page.manual-title')
                        )
                    }
                >
                    {t('appliance-page.see-manuals')}
                </span>
            )}
            {showHeatingAreaDocument() && (
                <a
                    className='link'
                    rel='noreferrer'
                    target='_blank'
                    href={`/file/${heatingAreaDocument.nodes[0].documents[0][currentLocale]}`}
                >
                    {heatingAreaDocument.nodes[0].title[currentLocale]}
                </a>
            )}
            {displayPrice && (
                <div className='price'>
                    {t('appliance-page.starting-at')}
                    <span>
                        {price?.regularPrice?.value === 0
                            ? '--'
                            : LocalePrice(
                                  currentLocale,
                                  price?.regularPrice?.value
                              )}
                    </span>
                </div>
            )}
            {appliance?.attributes?.TextPrix?.values?.[0] &&
                currentLocale !== 'en_US' && (
                    <div className='included'>
                        {
                            appliance?.attributes?.TextPrix?.values?.[0][
                                currentLocale
                            ]
                        }
                    </div>
                )}

            <div id='scroll-appliance-summary' />

            {isShowVisao && mobileDetect.isDesktop() && showVisaoButtons(false)}

            <ModalAppliancePreviewVisao
                isOpen={toggler3D}
                isStartAR={isStartAR}
                visaoId={visaoId!}
                appliance={appliance}
                handleModalClose={handleModalClose}
            />
        </div>
    );
}
